import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Table, Tag, App, Button, Empty } from 'antd';
import { useNavigate } from 'react-router-dom';
import { authHeaders } from '../api/headers';
import { apiUrl } from '../api/http';
import { MaintenanceStatusTag, ServiceStatusTag } from '../components/maintenance/MaintenanceStatusTags';

const serviceStatusColor = {
  IN_SERVICE: 'blue',
  DOWN: 'red',
  WAITING_ON_PARTS: 'orange',
  RETIRED: 'default',
};

function getServiceStatusTag(status) {
  if (!status) return null;

  const labelMap = {
    IN_SERVICE: 'In service',
    DOWN: 'Down',
    WAITING_ON_PARTS: 'Waiting on parts',
    RETIRED: 'Retired',
  };

  const key = String(status).toUpperCase();
  const color = serviceStatusColor[key] || 'default';
  const label = labelMap[key] || status;

  return (
    <Tag color={color} style={{ borderRadius: 999 }}>
      {label}
    </Tag>
  );
}

const pillBtnStyle = { borderRadius: 999 };

function valueOrBlank(value) {
  return value || value === 0 ? value : '';
}

function joinParts(parts) {
  return parts.map(valueOrBlank).filter(Boolean).join(' ');
}

export default function DraftsPage() {
  const navigate = useNavigate();
  const { modal, message } = App.useApp();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const forkliftByIdRef = useRef(new Map());
  const forkliftByUnitRef = useRef(new Map());

  const getHeaders = useCallback(() => {
    return authHeaders();
  }, []);

  const tryDelete = async (url, headers) => {
    const res = await fetch(apiUrl(url), { method: 'DELETE', headers });

    if (res.ok || res.status === 204) return { ok: true, res };

    let body = null;
    try {
      body = await res.json();
    } catch {
      body = null;
    }

    return { ok: false, res, body };
  };

  const tryGetJson = async (url, headers) => {
    const res = await fetch(apiUrl(url), { method: 'GET', headers });
    if (!res.ok) return { ok: false, res, body: null };
    let body = null;
    try {
      body = await res.json();
    } catch {
      body = null;
    }
    return { ok: true, res, body };
  };

  const tryPutJson = async (url, headers, payload) => {
    const res = await fetch(apiUrl(url), {
      method: 'PUT',
      headers,
      body: JSON.stringify(payload ?? {}),
    });

    if (res.ok) return { ok: true, res };

    let body = null;
    try {
      body = await res.json();
    } catch {
      body = null;
    }

    return { ok: false, res, body };
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const headers = getHeaders();
      const normUnit = (v) => String(v ?? '').trim().toUpperCase();

      const [mRes, rRes, fRes] = await Promise.allSettled([
        fetch(apiUrl('/api/maintenance/drafts'), { headers }),
        fetch(apiUrl('/api/repairs/drafts'), { headers }),
        fetch(apiUrl('/api/forklifts'), { headers }),
      ]);

      forkliftByIdRef.current = new Map();
      forkliftByUnitRef.current = new Map();

      if (fRes.status === 'fulfilled' && fRes.value.ok) {
        const data = await fRes.value.json();
        const list = Array.isArray(data) ? data : [];

        list.forEach((f) => {
          const id = f?.id ?? f?.forkliftId ?? f?.forklift_id ?? null;

          const unitVal =
            f?.unitId ??
            f?.unit_id ??
            f?.unitNumber ??
            f?.unit_number ??
            f?.unit ??
            null;

          const key = normUnit(unitVal);

          if (id != null) {
            forkliftByIdRef.current.set(String(id), f);
          }

          if (key) {
            const existing = forkliftByUnitRef.current.get(key);
            if (!existing) {
              forkliftByUnitRef.current.set(key, f);
            } else {
              const exStatus = String(
                existing?.status ??
                  existing?.serviceStatus ??
                  existing?.service_status ??
                  existing?.forkliftStatus ??
                  existing?.forklift_status ??
                  ''
              ).toUpperCase();

              const nxStatus = String(
                f?.status ??
                  f?.serviceStatus ??
                  f?.service_status ??
                  f?.forkliftStatus ??
                  f?.forklift_status ??
                  ''
              ).toUpperCase();

              if (exStatus === 'RETIRED' && nxStatus !== 'RETIRED') {
                forkliftByUnitRef.current.set(key, f);
              }
            }
          }
        });
      }

      const statusFromForklift = (forklift) => {
        return (
          forklift?.status ??
          forklift?.serviceStatus ??
          forklift?.service_status ??
          forklift?.forkliftStatus ??
          forklift?.forklift_status ??
          null
        );
      };

      const maintenanceStatusFromForklift = (forklift) => {
        return (
          forklift?.maintenanceStatus ??
          forklift?.maintenance_status ??
          forklift?.MaintenanceStatus ??
          forklift?.maintenance ??
          forklift?.Maintenance ??
          null
        );
      };

      const getForkliftForDraftRow = (unitValRaw, forkliftIdRaw) => {
        const forkliftId = forkliftIdRaw != null ? String(forkliftIdRaw) : '';
        if (forkliftId) {
          const f = forkliftByIdRef.current.get(forkliftId);
          if (f) return f;
        }

        const key = normUnit(unitValRaw);
        if (!key) return null;
        return forkliftByUnitRef.current.get(key) || null;
      };

      const getStatusForDraftRow = (unitValRaw, forkliftIdRaw) => {
        return statusFromForklift(getForkliftForDraftRow(unitValRaw, forkliftIdRaw));
      };

      const getForkliftDetailsForDraftRow = (unitValRaw, forkliftIdRaw) => {
        const f = getForkliftForDraftRow(unitValRaw, forkliftIdRaw);
        return {
          make: f?.make ?? f?.Make ?? null,
          model: f?.model ?? f?.Model ?? null,
          serialNumber:
            f?.serialNumber ??
            f?.serial ??
            f?.SerialNumber ??
            f?.serial_number ??
            f?.Serial_Number ??
            null,
          hours:
            f?.hours ??
            f?.hourMeter ??
            f?.hour_meter ??
            f?.Hours ??
            f?.HourMeter ??
            f?.Hour_Meter ??
            null,
          maintenanceStatus: maintenanceStatusFromForklift(f),
        };
      };

      const toTs = (d) => {
        const raw = d?.updatedAt || d?.createdAt || d?.created_at;
        const t = raw ? Date.parse(raw) : 0;
        return Number.isFinite(t) ? t : 0;
      };

      const toDisplay = (ts) => {
        if (!ts) return '';
        return new Date(ts).toLocaleString();
      };

      let maintenance = [];
      if (mRes.status === 'fulfilled' && mRes.value.ok) {
        const data = await mRes.value.json();
        maintenance = (Array.isArray(data) ? data : []).map((d) => {
          const ts = toTs(d);
          const unitVal = d.unitId ?? '';
          const forkliftId = d.forkliftId ?? d.forklift_id ?? null;

          return {
            id: d.id,
            type: 'Maintenance',
            unitId: unitVal,
            forkliftId,
            lastSavedTs: ts,
            lastSaved: toDisplay(ts),
            serviceStatus: getStatusForDraftRow(unitVal, forkliftId),
            ...getForkliftDetailsForDraftRow(unitVal, forkliftId),
            route: unitVal
              ? `/maintenance/${encodeURIComponent(unitVal)}?draftId=${d.id}`
              : null,
          };
        });
      }

      let repairs = [];
      if (rRes.status === 'fulfilled' && rRes.value.ok) {
        const data = await rRes.value.json();
        repairs = (Array.isArray(data) ? data : []).map((d) => {
          const ts = toTs(d);
          const unitVal = d.unitId ?? '';
          const forkliftId = d.forkliftId ?? d.forklift_id ?? null;

          return {
            id: d.id,
            type: 'Repair',
            unitId: unitVal,
            forkliftId,
            lastSavedTs: ts,
            lastSaved: toDisplay(ts),
            serviceStatus: getStatusForDraftRow(unitVal, forkliftId),
            ...getForkliftDetailsForDraftRow(unitVal, forkliftId),
            route: unitVal
              ? `/repairs/${encodeURIComponent(unitVal)}?draftId=${d.id}`
              : null,
          };
        });
      }

      const merged = [...maintenance, ...repairs].sort(
        (a, b) => (b.lastSavedTs || 0) - (a.lastSavedTs || 0)
      );

      setRows(merged);
    } catch {
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [getHeaders]);

  useEffect(() => {
    load();
  }, [load]);

  const fetchDraftDetails = async (record) => {
    const headers = getHeaders();
    const type = String(record?.type || '').toLowerCase();
    const id = encodeURIComponent(record?.id);

    const candidates =
      type === 'maintenance'
        ? [`/api/maintenance/draft/${id}`, `/api/maintenance/drafts/${id}`]
        : [`/api/repairs/drafts/${id}`, `/api/repairs/draft/${id}`];

    let lastError = null;

    for (const url of candidates) {
      const out = await tryGetJson(url, headers);
      if (out.ok && out.body) return out.body;

      if (out?.res?.status === 404) {
        lastError = out;
        continue;
      }

      lastError = out;
    }

    const msg =
      lastError?.body?.message ||
      lastError?.body?.error ||
      `Unable to load draft details (${lastError?.res?.status || 'unknown'}).`;
    throw new Error(msg);
  };

  const updateForkliftStatusForAudit = async (forkliftIdRaw, unitIdRaw, nextStatusRaw) => {
    const headers = getHeaders();
    const normUnit = (v) => String(v ?? '').trim().toUpperCase();

    const allowed = ['IN_SERVICE', 'DOWN', 'WAITING_ON_PARTS'];
    const safeStatus = allowed.includes(String(nextStatusRaw).toUpperCase())
      ? String(nextStatusRaw).toUpperCase()
      : 'IN_SERVICE';

    let forkliftId = forkliftIdRaw != null ? String(forkliftIdRaw) : '';

    if (!forkliftId) {
      const key = normUnit(unitIdRaw);
      const fallback = key ? forkliftByUnitRef.current.get(key) : null;
      forkliftId =
        fallback?.id ??
        fallback?.forkliftId ??
        fallback?.forklift_id ??
        '';
      forkliftId = forkliftId ? String(forkliftId) : '';
    }

    if (!forkliftId) {
      throw new Error('Forklift not found for this draft.');
    }

    const forklift = forkliftByIdRef.current.get(forkliftId);

    const currentStatus =
      forklift?.status ??
      forklift?.serviceStatus ??
      forklift?.service_status ??
      forklift?.forkliftStatus ??
      forklift?.forklift_status ??
      null;

    const cur = String(currentStatus ?? '').toUpperCase();
    if (cur && cur === safeStatus) return;

    const url = `/api/forklifts/${encodeURIComponent(forkliftId)}`;

    let out = await tryPutJson(url, headers, { status: safeStatus });

    if (!out.ok) {
      const payload = { ...(forklift || {}), status: safeStatus };
      out = await tryPutJson(url, headers, payload);
    }

    if (!out.ok) {
      const msg =
        out?.body?.message ||
        out?.body?.error ||
        `Status update failed (${out?.res?.status || 'unknown'}).`;
      throw new Error(msg);
    }

    forkliftByIdRef.current.set(forkliftId, { ...(forklift || {}), status: safeStatus });

    setRows((prev) =>
      prev.map((r) => {
        const rowForkliftId = r?.forkliftId != null ? String(r.forkliftId) : '';
        if (rowForkliftId && rowForkliftId === forkliftId) {
          return { ...r, serviceStatus: safeStatus };
        }
        return r;
      })
    );
  };

  const updateStatusOnlyLikeSaveForLater = async (record, nextStatus, draftDetails) => {
    const draftForkliftId =
      draftDetails?.forkliftId ??
      draftDetails?.forklift_id ??
      record?.forkliftId ??
      null;

    await updateForkliftStatusForAudit(draftForkliftId, record?.unitId, nextStatus);
  };

  const cancelDraft = async (record) => {
    const headers = getHeaders();
    const type = String(record?.type || '').toLowerCase();
    const id = encodeURIComponent(record?.id);

    const candidates =
      type === 'maintenance'
        ? [`/api/maintenance/draft/${id}`, `/api/maintenance/drafts/${id}`]
        : [`/api/repairs/drafts/${id}`];

    let lastError = null;

    for (const url of candidates) {
      const out = await tryDelete(url, headers);

      if (out.ok) return;

      if (out?.res?.status === 404) {
        lastError = out;
        continue;
      }

      const msg =
        out?.body?.message ||
        out?.body?.error ||
        `Cancel failed (${out?.res?.status || 'unknown'}).`;
      throw new Error(msg);
    }

    const msg =
      lastError?.body?.message ||
      lastError?.body?.error ||
      'Cancel failed (no matching DELETE endpoint found).';
    throw new Error(msg);
  };

  const confirmCancel = (record) => {
    const allowed = ['IN_SERVICE', 'DOWN', 'WAITING_ON_PARTS'];

    let picked = String(record?.serviceStatus || 'IN_SERVICE').toUpperCase();
    if (!allowed.includes(picked)) picked = 'IN_SERVICE';

    let modalRef = null;

    const renderContent = () => (
      <div>
        <div style={{ marginBottom: 10 }}>
          This will delete the draft and it cannot be undone.
        </div>

        <div style={{ fontWeight: 700, marginBottom: 6 }}>
          Set forklift status after cancel:
        </div>

        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 8 }}>
          <Button
            type={picked === 'IN_SERVICE' ? 'primary' : 'default'}
            style={pillBtnStyle}
            onClick={() => {
              picked = 'IN_SERVICE';
              modalRef?.update?.({ content: renderContent() });
            }}
          >
            In service
          </Button>

          <Button
            type={picked === 'DOWN' ? 'primary' : 'default'}
            style={pillBtnStyle}
            onClick={() => {
              picked = 'DOWN';
              modalRef?.update?.({ content: renderContent() });
            }}
          >
            Down
          </Button>

          <Button
            type={picked === 'WAITING_ON_PARTS' ? 'primary' : 'default'}
            style={pillBtnStyle}
            onClick={() => {
              picked = 'WAITING_ON_PARTS';
              modalRef?.update?.({ content: renderContent() });
            }}
          >
            Waiting on parts
          </Button>
        </div>

        <div style={{ fontSize: 12, color: '#808080', marginTop: 10 }} />
      </div>
    );

    modalRef = modal.confirm({
      title: 'Cancel this draft?',
      content: renderContent(),
      okText: 'Cancel Draft',
      okButtonProps: { danger: true },
      cancelText: 'Keep Draft',
      onOk: async () => {
        try {
          const draftDetails = await fetchDraftDetails(record);

          await updateStatusOnlyLikeSaveForLater(record, picked, draftDetails);
          await cancelDraft(record);

          setRows((prev) =>
            prev.filter((r) => !(r.type === record.type && r.id === record.id))
          );
          message.success('Draft canceled and forklift status updated.');
        } catch (err) {
          message.error(err?.message || 'Cancel failed.');
          throw err;
        }
      },
    });
  };

  const columns = [
    { title: 'Type', dataIndex: 'type', key: 'type' },
    { title: 'Unit ID', dataIndex: 'unitId', key: 'unitId' },
    { title: 'Make', dataIndex: 'make', key: 'make', render: valueOrBlank },
    { title: 'Model', dataIndex: 'model', key: 'model', render: valueOrBlank },
    { title: 'Serial #', dataIndex: 'serialNumber', key: 'serialNumber', render: valueOrBlank },
    { title: 'Last Saved', dataIndex: 'lastSaved', key: 'lastSaved' },
    {
      title: 'Service',
      dataIndex: 'serviceStatus',
      key: 'serviceStatus',
      render: (status) => getServiceStatusTag(status),
    },
    {
      title: 'Action',
      key: 'actions',
      width: 96,
      align: 'left',
      render: (_, record) => (
        <Button
          data-action="cancel"
          onMouseDownCapture={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
          onClickCapture={(e) => {
            e.preventDefault();
            e.stopPropagation();
            confirmCancel(record);
          }}
          className="forklifts-page-table-action technician-drafts-cancel-action"
        >
          Cancel
        </Button>
      ),
    },
  ];

  const renderMobileDrafts = () => (
    <div className="technician-drafts-mobile-list" aria-label="Drafts list">
      {rows.length ? rows.map((record) => {
        const makeModel = joinParts([record.make, record.model]);
        const unitAndModel = [record.unitId || 'Unit unavailable', makeModel]
          .filter(Boolean)
          .join(' · ');
        const serial = valueOrBlank(record.serialNumber);
        const hours = valueOrBlank(record.hours);
        const metadata = [serial ? `Serial ${serial}` : '', hours || hours === 0 ? `${hours} hrs` : '']
          .filter(Boolean)
          .join(' · ');

        return (
        <article
          key={`${record.type}-${record.id}`}
          className="maintenance-forklift-mobile-card technician-drafts-mobile-card"
          onClick={(e) => {
            if (e?.target?.closest?.('[data-action="cancel"]')) return;
            if (record.route) navigate(record.route);
          }}
          style={{ cursor: record.route ? 'pointer' : 'default' }}
        >
          <div className="maintenance-forklift-mobile-card-header">
            <span className="maintenance-forklift-mobile-unit">
              {record.type} Draft
            </span>
            <span className="maintenance-forklift-mobile-badges">
              <ServiceStatusTag status={record.serviceStatus} />
              <MaintenanceStatusTag status={record.maintenanceStatus} />
            </span>
          </div>

          <div className="maintenance-forklift-mobile-details">
            <div className="maintenance-forklift-mobile-model">{unitAndModel}</div>
            {metadata ? <div className="maintenance-forklift-mobile-meta">{metadata}</div> : null}
            <div className="maintenance-forklift-mobile-meta">Last Saved: {record.lastSaved || '—'}</div>
          </div>

          <div className="forklifts-page-mobile-actions technician-forklifts-mobile-actions technician-drafts-mobile-actions">
            <Button
              data-action="cancel"
              className="technician-drafts-cancel-action technician-drafts-mobile-cancel"
              onMouseDownCapture={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
              onClickCapture={(e) => {
                e.preventDefault();
                e.stopPropagation();
                confirmCancel(record);
              }}
            >
              Cancel
            </Button>
          </div>
        </article>
        );
      }) : (
        <Empty description="No drafts" image={Empty.PRESENTED_IMAGE_SIMPLE} />
      )}
    </div>
  );

  return (
    <div className="technician-drafts-root">
      <div className="forklifts-page-table-wrap technician-drafts-table-wrap">
        <Table
          rowKey={(r) => `${r.type}-${r.id}`}
          dataSource={rows}
          columns={columns}
          pagination={false}
          size="middle"
          loading={loading}
          scroll={{ x: 'max-content' }}
          locale={{ emptyText: 'No drafts' }}
          onRow={(record) => ({
            onClick: (e) => {
              if (e?.target?.closest?.('[data-action="cancel"]')) return;
              if (record.route) navigate(record.route);
            },
            style: { cursor: record.route ? 'pointer' : 'default' },
          })}
        />
      </div>

      {renderMobileDrafts()}
    </div>
  );
}
